import { useContext, useEffect, useMemo, useState, ReactNode } from 'react';

import { ADModalBodyNoScroll, AdvancedDragModal } from 'src/componentsUtils/AdvancedDragModal';
import { useWindowContext } from 'src/WindowManager/useWindowContext';
import { ManagedWindow, openWindow, WindowId } from 'src/WindowManager/WindowState';
import { PersistedFileListView, ListItemSpec, ActionSpec } from 'src/common/components/PersistedFileListView';
import { SettingsManager, Localization, ViamapPersistenceLayer, AppMessagesContext, actionSetErrorMessage, Logger, actionSetInfoMessage } from '@viamap/viamap2-common';

export const ManageDocuments = (props: {
  managedWindow?: ManagedWindow,
  showWindow?: boolean,
  callbackOnClose: () => void,
  projectName: string,
  projectId?: string,
  bucket: string,
  path: string,
  createUniqueFileName: boolean, /// if true a unique file name will be created when uploading a file
  callBackOnAction?: (action: string, parameters: any) => void,
  uploadAvailable: boolean,
  deleteAvailable: boolean,
  downloadAvailable: boolean,
  openAvailable: boolean,
  sortField?: string,
  copyLinkAvailable?: boolean,
  linksAvailable?: boolean, /// if true, allows adding and managing links in addition to files
  additionalPaths?: { path: string }[],
  toolbarContent?: ReactNode,
  resolveElementName?: (elementId: string) => string | undefined,
  filterProjectRootOnly?: boolean, /// if true, filters project root paths to only show files directly in root (not in element subfolders)
}) => {

  const { dispatch: windowDispatch } = useWindowContext();
  const { dispatch: appMessagesDispatch } = useContext(AppMessagesContext);

  let [peristenceInterfaceInstance] = useState<ViamapPersistenceLayer>(new ViamapPersistenceLayer(props.bucket));
  let [listItems, setListItems] = useState<any[]>([]);

  const additionalPathsKey = useMemo(() => (props.additionalPaths || []).map(p => p.path).join("|"), [props.additionalPaths]);

  useEffect(() => {
    refreshListItemsData()
      .catch((error) => {
        Logger.logError("ManageDocuments", "useEffect", error.message || error);
      });
  }, [props.path, additionalPathsKey, props.filterProjectRootOnly]);

  function copyToClipBoard(textToCopy: string) {
    var tempText = document.createElement("input");
    tempText.value = textToCopy;
    document.body.appendChild(tempText);
    tempText.select();

    document.execCommand("copy");
    document.body.removeChild(tempText);
    // alert("Copied"+textToCopy);
  }

  function AnchorHandler(blob: Blob, fileName?: string) {
    let url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    if (fileName) {
      a.download = fileName;
    } else {
      a.target = "_blank";
    }
    a.href = url;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function persistFile(fileNameExtension: string, fileName: string, extension: string, currentFile: File) {

    return new Promise<void>((resolve, reject) => {

      // Clear the "files" value such that the same file can be imported again/twice
      let e: HTMLElement | null = document.getElementById("files");
      if (e) {
        (e as HTMLInputElement).value = "";
      }
      let reader = new FileReader();
      reader.onerror = (ex /*:FileReaderProgressEvent */) => {
        appMessagesDispatch(actionSetErrorMessage(Localization.getFormattedText("Could not read file {file}. Lines read {number}", { file: currentFile.name, number: ex.loaded })));
      };
      reader.onload = () => {
        try {
          let AWSMetaData = {
            filename: encodeURIComponent(currentFile.name),
            size: currentFile.size.toFixed(0),
            lastModified: currentFile.lastModified.toFixed(0),
            'cache-control': 'no-cache',
            'inherit': 'true'
          };
          let data = currentFile;
          let objRef = currentFile.name;
          if (props.createUniqueFileName) {
            objRef = peristenceInterfaceInstance.generateUniqueObjectRef(extension);
          }
          let path = props.path;
          if (!path.endsWith('/')) {
            path = path + '/';
          }
          // alert(`Uploading to: ${path + objRef}`);

          peristenceInterfaceInstance.uploadObjectS3(
            path + objRef,
            data,
            AWSMetaData
          )
            .then(() => {
              Logger.logInfo("PersistenceItemListView", "PersistFile", "User uploaded a file: " + currentFile.name + "." + extension)
              refreshListItemsData()
                .then(() => {
                  resolve();
                })
                .catch((error) => { reject(error); })
            })
            .catch((err) => {
              appMessagesDispatch(actionSetErrorMessage("Error:" + err.message));
              reject(err);
            });

        } catch (error: any) {
          // Show error message
          appMessagesDispatch(actionSetErrorMessage("Error:" + error.message));
          reject(error);
        }
      };
      reader.readAsArrayBuffer(currentFile);
    });
  }

  function persistLink(url: string, displayName?: string) {
    return new Promise<void>((resolve, reject) => {
      try {
        if (!url || !url.trim()) {
          appMessagesDispatch(actionSetErrorMessage("Please provide a valid URL"));
          reject(new Error("Empty URL"));
          return;
        }

        let validUrl = url.trim();
        if (!validUrl.startsWith('http://') && !validUrl.startsWith('https://')) {
          validUrl = 'https://' + validUrl;
        }

        const timestamp = Date.now();
        const linkFileName = `_link_${timestamp}.json`;

        const trimmedDisplayName = displayName?.trim();

        let path = props.path;
        if (!path.endsWith('/')) {
          path = path + '/';
        }

        const linkData: { url: string, timestamp: string, name?: string } = {
          url: validUrl,
          timestamp: timestamp.toString()
        };
        if (trimmedDisplayName) {
          linkData.name = trimmedDisplayName;
        }
        const jsonContent = JSON.stringify(linkData);
        const blob = new Blob([jsonContent], { type: 'application/json' });

        const AWSMetaData: Record<string, string> = {
          filename: validUrl,
          size: blob.size.toFixed(0),
          lastModified: timestamp.toFixed(0),
          'cache-control': 'no-cache',
          'inherit': 'true',
          'isLink': 'true'
        };


        peristenceInterfaceInstance.uploadObjectS3(
          path + linkFileName,
          blob,
          AWSMetaData
        )
          .then(() => {
            Logger.logInfo("ManageDocuments", "PersistLink", "User added a link: " + validUrl);
            refreshListItemsData()
              .then(() => {
                resolve();
              })
              .catch((error) => { reject(error); });
          })
          .catch((err) => {
            appMessagesDispatch(actionSetErrorMessage("Error:" + err.message));
            reject(err);
          });

      } catch (error: any) {
        appMessagesDispatch(actionSetErrorMessage("Error:" + error.message));
        reject(error);
      }
    });
  }


  function refreshListItemsData() {
    return new Promise<void>((resolve, reject) => {
      const additionalPaths = props.additionalPaths || [];
      const allPaths = [props.path, ...additionalPaths.map(p => p.path)].filter(Boolean);
      if (allPaths.length === 0) {
        setListItems([]);
        resolve();
        return;
      }

      Promise.all(allPaths.map(prefix =>
        peristenceInterfaceInstance.getListingS3(prefix)
          .then((data: any[]) => ({ prefix, data }))
      ))
        .then(async (results) => {

          let uniqueTruncatedItems: { [prefix: string]: { item: any, prefix: string, isFile: boolean, isFolder: boolean, element: string, sourcePrefix: string, label?: string, isProjectLevel?: boolean } } = {};
          results.forEach(({ prefix, data }) => {
            const elementsCount = prefix.split('/').length;

            const isProjectRootPath = props.projectId && prefix.endsWith(`${props.projectId}/`);

            data.forEach((item: any) => {
              if (props.filterProjectRootOnly && isProjectRootPath && props.projectId) {
                const keySegments = item.Key.split("/").filter(Boolean);
                const projectIdx = keySegments.indexOf(props.projectId);
                if (projectIdx >= 0) {
                  const segmentsAfterProject = keySegments.length - (projectIdx + 1);
                  if (segmentsAfterProject > 1) {
                    return;
                  }
                }
              }
              const label = (() => {
                const keySegments = item.Key.split("/").filter(Boolean);
                if (props.projectId) {
                  const projectIdx = keySegments.indexOf(props.projectId);
                  if (projectIdx >= 0) {
                    const segmentsAfterProject = keySegments.length - (projectIdx + 1);

                    if (segmentsAfterProject > 1) {
                      const elementId = keySegments[projectIdx + 1];
                      return { label: props.resolveElementName?.(elementId) || elementId, isProjectLevel: false };
                    }
                    return { label: props.projectName, isProjectLevel: true };
                  }
                }
                const sourceSegments = prefix.split("/").filter(Boolean);
                const fallbackFolder = sourceSegments[sourceSegments.length - 1] || prefix;
                return { label: props.resolveElementName?.(fallbackFolder) || fallbackFolder, isProjectLevel: false };
              })();
              let isFolder = item.Key.endsWith('/');
              if (isFolder && item.Key === prefix) {
                // do not include the folder that is the prefix
                return;
              }
              if (isFolder) {
                // don't include folders for now
                return;
              }
              let isFile = !isFolder;
              let split = item.Key.split('/');
              let itemCount = split.length;
              let prefix2 = split.slice(0, elementsCount).join('/');
              let element = split[itemCount - 1];
              uniqueTruncatedItems[item.Key] = {
                item: item,
                prefix: prefix2,
                isFile: isFile,
                isFolder: isFolder,
                element: element,
                sourcePrefix: prefix,
                label: label.label,
                isProjectLevel: label.isProjectLevel
              }
            })
          })

          let promises = Object.keys(uniqueTruncatedItems).map(async (uniqueKey: any) => {
            let uniqueItem = uniqueTruncatedItems[uniqueKey];
            let item = uniqueItem.item;
            const displayPath = uniqueItem.label || uniqueItem.element || uniqueKey;
            const isProjectLevel = uniqueItem.isProjectLevel || false;
            let result: ListItemSpec | null;
            if (uniqueItem.isFolder) {
              result = {
                path: displayPath,
                size: item.Size,
                contentType: "directory",
                icon: undefined,
                filename: uniqueKey,
                actions: [],
                isProjectLevel: isProjectLevel,
              };
              let action: ActionSpec = {
                title: "Select",
                handler: () => {
                  if (props.callBackOnAction) props.callBackOnAction("Select", { itemRef: uniqueKey });
                }
              }
              result.actions.push(action);
            } else {
              let itemData = await peristenceInterfaceInstance.getObjectS3(item.Key);
              const isLink = itemData.Metadata?.isLink === 'true' || uniqueItem.element.startsWith('_link_');

              if (isLink) {
                try {
                  const linkContent = JSON.parse(new TextDecoder().decode(new Uint8Array(itemData.Body as Buffer)));
                  const linkUrl = linkContent.url || itemData.Metadata?.filename || uniqueItem.element;
                  const linkDisplayName = linkContent.name || itemData.Metadata?.linkdisplayname;

                  result = {
                    path: displayPath,
                    storagePath: item.Key,
                    size: item.Size,
                    contentType: "link",
                    icon: undefined,
                    filename: linkDisplayName || linkUrl,
                    lastmodified: linkContent.timestamp || itemData.Metadata?.lastModified || item.LastModified?.getTime?.()?.toString() || Date.now().toString(),
                    linkUrl: linkUrl,
                    linkDisplayName: linkDisplayName,
                    ...itemData.Metadata,
                    actions: [],
                    isProjectLevel: isProjectLevel,
                  }

                  if (props.openAvailable !== false) {
                    let action: ActionSpec = {
                      title: "Open",
                      handler: () => {
                        window.open(linkUrl, '_blank', 'noopener,noreferrer');
                      }
                    }
                    result.actions.push(action);
                  }

                  if (props.deleteAvailable) {
                    let action: ActionSpec = {
                      title: "Delete",
                      handler: () => {
                        const linkNameToShow = linkDisplayName || linkUrl;
                        windowDispatch(openWindow(WindowId.ConfirmationDialog, {
                          header: `Delete link`,
                          children: Localization.getFormattedText("Are you sure you want to delete the link '{linkName}'?", { linkName: linkNameToShow }),
                          onSubmit: async () => {
                            peristenceInterfaceInstance.deleteObjectS3(item.Key)
                              .then(() => {
                                refreshListItemsData();
                              })
                              .catch((err: any) => {
                                appMessagesDispatch(actionSetErrorMessage("Error deleting link: " + (err.message || err)));
                              });
                          }
                        }));
                      }
                    }
                    result.actions.push(action);
                  }
                } catch (error: any) {
                  Logger.logError("ManageDocuments", "refreshListItemsData", "Error parsing link JSON: " + error.message);
                  return null;
                }
              } else {
                const fileResult: ListItemSpec = {
                  path: displayPath,
                  storagePath: item.Key,
                  size: item.Size,
                  contentType: itemData.ContentType!,
                  icon: itemData.Body,
                  filename: itemData.Metadata?.filename || uniqueItem.element,
                  ...itemData.Metadata,
                  actions: [],
                  isProjectLevel: isProjectLevel,
                }

                // Add Actions in the order they should be displayed

                if (props.copyLinkAvailable) {
                  let action: ActionSpec = {
                    title: "CopyLinkToClipboard",
                    handler: () => {
                      let prefix = SettingsManager.getSystemSetting("s3bucketUrlPrefix");
                      copyToClipBoard(prefix + (fileResult.storagePath || fileResult.path));
                      appMessagesDispatch(actionSetInfoMessage(Localization.getText("CopyLinkToClipboardConfirmation")));
                    }
                  }
                  fileResult.actions.push(action);
                }
                if (props.openAvailable !== false) {
                  let action: ActionSpec = {
                    title: "Open",
                    handler: () => {
                      let blob = new Blob([new Uint8Array((itemData.Body as Buffer))], { type: itemData.ContentType });
                      AnchorHandler(blob);
                    }
                  }
                  fileResult.actions.push(action);
                }

                if (props.downloadAvailable !== false) {
                  let action: ActionSpec = {
                    title: "Download",
                    handler: () => {
                      let blob = new Blob([new Uint8Array((itemData.Body as Buffer))], { type: itemData.ContentType });
                      let path = item.Key;
                      let fileName = itemData.Metadata!.filename || path.split('/').at(-1) || "noname";
                      AnchorHandler(blob, fileName);
                    }
                  }
                  fileResult.actions.push(action);
                }


                if (props.deleteAvailable) {
                  let action: ActionSpec = {
                    title: "Delete",
                    handler: () => {
                      windowDispatch(openWindow(WindowId.ConfirmationDialog, {
                        header: `Delete file`,
                        children: Localization.getFormattedText("Are you sure you want to delete '{fileName}'?", { fileName: fileResult.filename }),
                        onSubmit: async () => {
                          peristenceInterfaceInstance.deleteObjectS3(item.Key)
                            .then(() => {
                              refreshListItemsData();
                            })
                            .catch((err: any) => {
                              appMessagesDispatch(actionSetErrorMessage("Error deleting item: " + (err.message || err)));
                            });
                        }
                      }));
                    }
                  }

                  fileResult.actions.push(action);
                };
                result = fileResult;
              }
            }
            return result;
          });
          const itemList = await Promise.all(promises);
          const filteredList = itemList.filter((item): item is ListItemSpec => item !== null);
          setListItems(filteredList);
          resolve();
        })
        .catch(error => {
          appMessagesDispatch(actionSetErrorMessage("Error getting list contents: " + error));
          reject(error);
        })

    });
  }


  return (
    <AdvancedDragModal
      PosDefault={{ left: "50%", top: "50%", width: "950px", height: "600px", transform: "translate(-50%, -50%)" }}
      window={props.managedWindow}
      topUpdate={props.managedWindow?.updateCounter}
      saveKey="ManageDocuments"
      title={`${Localization.getText("Documents")} - ${props.projectName}`}
      allowUserMiniMize
      allowFullscreen
      onClose={() => props.callbackOnClose()}
    >
      <ADModalBodyNoScroll templateRows={"1fr"} >
        <PersistedFileListView
          title={"Documents"}
          uploadAvailable={props.uploadAvailable}
          keysToDisplayWithHeaders={{
            "path": "Element",
            "filename": "File name",
            "lastmodified": "Last modified",
            // "contentType": "Type", 
            "actions": Localization.getText("Actions")
          }}
          listItems={listItems}
          sortField={"Key"}
          callBackOnAction={(action: string, parameters: any) => {
            alert(`Got action callback: ${action}, ${JSON.stringify(parameters)}`);
          }}
          persistFile={(fileNameExtension: string, fileName: string, extension: string, currentFile: File) => persistFile(fileNameExtension, fileName, extension, currentFile)}
          linksAvailable={props.linksAvailable}
          persistLink={persistLink}
          toolbarContent={props.toolbarContent}
        />
      </ADModalBodyNoScroll>
    </AdvancedDragModal>

  );
}



